import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import googleG from '../assets/google-g.svg';
import { api } from '../lib/api';

export interface Reviews {
  rating: number;
  count: number;
  profileUrl: string;
  items: { author: string; text: string }[];
}

/** Nota e avaliações do Google, mantidas no painel. Sem dado, os componentes não aparecem. */
export function useReviews() {
  return useQuery({
    queryKey: ['chat', 'reviews'],
    queryFn: () => api.get<Reviews | null>('/chat/reviews'),
    staleTime: 10 * 60_000,
    retry: false,
  }).data;
}

const nota = (r: number) => r.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Cinco estrelas preenchidas na proporção da nota (4,9 deixa a última quase cheia, como no Google). */
function Stars({ rating, size = 16 }: { rating: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  const row = (cor: string) => (
    <svg width={size * 5} height={size} viewBox="0 0 120 24" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <path
          key={i}
          transform={`translate(${i * 24} 0)`}
          fill={cor}
          d="M12 1.8l3.1 6.6 7.2.8-5.4 4.9 1.5 7.1L12 17.6l-6.4 3.6 1.5-7.1L1.7 9.2l7.2-.8z"
        />
      ))}
    </svg>
  );
  return (
    <span className="rv-stars" style={{ width: size * 5, height: size }}>
      {row('#dadce0')}
      <span className="rv-stars-fill" style={{ width: `${pct}%` }}>
        {row('#fbbc04')}
      </span>
    </span>
  );
}

const resumo = (r: Reviews) => `Nota ${nota(r.rating)} de 5 no Google, com ${r.count} avaliações`;

/** Selo compacto: celular e balão do site. */
export function ReviewsBadge({ r }: { r: Reviews }) {
  return (
    <a className="rv-badge" href={r.profileUrl} target="_blank" rel="noreferrer noopener" aria-label={`${resumo(r)}. Abrir no Google`}>
      <img src={googleG} alt="" width="16" height="16" />
      <strong>{nota(r.rating)}</strong>
      <Stars rating={r.rating} size={13} />
      <span>
        {r.count} avaliações no Google
      </span>
    </a>
  );
}

/** Cartão da coluna lateral (desktop), com as avaliações passando uma de cada vez. */
export function ReviewsCard({ r }: { r: Reviews }) {
  const [i, setI] = useState(0);
  const total = r.items.length;

  useEffect(() => {
    if (total < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setI((n) => (n + 1) % total), 8000);
    return () => clearInterval(t);
  }, [total]);

  const atual = r.items[i % Math.max(total, 1)];
  return (
    <section className="rv-card" aria-label="Avaliações no Google">
      <header>
        <img src={googleG} alt="" width="22" height="22" />
        <div>
          <p className="rv-score">
            <strong>{nota(r.rating)}</strong> <Stars rating={r.rating} />
          </p>
          <p className="rv-count">{r.count} avaliações no Google</p>
        </div>
      </header>
      {atual && (
        <figure className="rv-quote" aria-live="polite">
          <blockquote>“{atual.text}”</blockquote>
          <figcaption>{atual.author}</figcaption>
        </figure>
      )}
      <footer>
        {total > 1 && (
          <span className="rv-dots" role="group" aria-label="Escolher avaliação">
            {r.items.map((it, n) => (
              <button key={it.author} type="button" aria-label={`Avaliação de ${it.author}`} aria-pressed={n === i % total} onClick={() => setI(n)} />
            ))}
          </span>
        )}
        <a href={r.profileUrl} target="_blank" rel="noreferrer noopener">
          Ver todas no Google
        </a>
      </footer>
    </section>
  );
}
